import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext.js';

const ClientForm = ({ 
  showModal, 
  editingClient, 
  formData, 
  setFormData, 
  cuiData,
  cuiLoading,
  onFetchCui,
  onSubmit, 
  onClose 
}) => {
  const { t } = useLanguage();

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-5 mx-auto p-6 border w-full max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-6">
            {editingClient ? t('editClient') : t('addClient')}
          </h3>
          <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Client Type Selection */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('clientType')}
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="clientType"
                      value="INDIVIDUAL"
                      checked={formData.clientType === 'INDIVIDUAL'}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        clientType: e.target.value,
                        company: e.target.value === 'INDIVIDUAL' ? '' : formData.company
                      })}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">{t('individual')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="clientType"
                      value="BUSINESS_ENTITY"
                      checked={formData.clientType === 'BUSINESS_ENTITY'}
                      onChange={(e) => setFormData({ ...formData, clientType: e.target.value })}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">{t('business')}</span>
                  </label>
                </div>
              </div>

              {/* Basic Information */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('name')} *
                  </label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                {formData.clientType === 'BUSINESS_ENTITY' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t('company')} *
                    </label>
                    <input
                      type="text"
                      required={formData.clientType === 'BUSINESS_ENTITY'}
                      className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    />
                  </div>
                )}
              </div>

              {/* Contact Information */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-4">{t('contactInformation')}</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t('email')} *
                    </label>
                    <input
                      type="email"
                      required
                      className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t('phone')}
                    </label>
                    <input
                      type="tel"
                      placeholder="+40 7XX XXX XXX"
                      className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('preferredContact')}
                    </label>
                    <select
                      className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                      value={formData.preferredContact}
                      onChange={(e) => setFormData({ ...formData, preferredContact: e.target.value })}
                    >
                      <option value="EMAIL">{t('email')}</option>
                      <option value="PHONE">{t('phone')}</option>
                      <option value="ALL">{t('emailAndPhone')}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Business Information - Only for Business Entities */}
              {formData.clientType === 'BUSINESS_ENTITY' && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-4">{t('businessInformation')}</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {t('taxIdentifier')} (CUI) *
                      </label>
                      <div className="mt-1 flex gap-2">
                        <input
                          type="text"
                          required={formData.clientType === 'BUSINESS_ENTITY'}
                          className="block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                          placeholder="12345678"
                          value={formData.taxIdentifier}
                          onChange={(e) => setFormData({ ...formData, taxIdentifier: e.target.value })}
                        />
                        <button
                          type="button"
                          onClick={onFetchCui}
                          disabled={cuiLoading || !formData.taxIdentifier}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {cuiLoading ? 'Verifying...' : 'Verify CUI'}
                        </button>
                      </div>
                      
                      {/* CUI Verification Status */}
                      {cuiData && (
                        <div className="mt-2 p-3 bg-green-100 border border-green-400 rounded-md">
                          <p className="text-sm text-green-700">
                            ✓ CUI verified successfully! Company: {cuiData.name}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {t('regCom')}
                      </label>
                      <input
                        type="text"
                        className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                        placeholder="J40/1234/2020"
                        value={formData.regCom}
                        onChange={(e) => setFormData({ ...formData, regCom: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {t('city')}
                      </label>
                      <input
                        type="text"
                        className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="vatPayer"
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        checked={formData.vatPayer}
                        onChange={(e) => setFormData({ ...formData, vatPayer: e.target.checked })}
                      />
                      <label htmlFor="vatPayer" className="ml-2 block text-sm text-gray-900">
                        {t('vatPayer')}
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Address Information */}
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-4">{t('addressInformation')}</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t('address')}
                  </label>
                  <textarea
                    rows={5}
                    className="mt-1 block w-full border-gray-300 border py-2 px-3 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Form Actions - Full Width */}
            <div className="lg:col-span-2 flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                className="px-6 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                {editingClient ? t('updateClient') : t('addClient')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientForm;
